import React from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import {
  Grid,
  Alert,
  Switch,
  Checkbox,
  FormControlLabel,
  Divider,
  Typography,
  TextField,
  Button,
  InputAdornment,
  MenuItem,
  Chip,
  Stack,
  Tooltip,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { setQrSlug, encryptBesteron, listCustomerMenus, getWebDomains, setWebDomains } from "app/services/api/qrOrders";
import { generateToken } from "app/services/api/management";
import { UploadComponent } from "app/pages/presentation/module/form/types";
import { uploadToImagekit } from "app/services/api/media";

const QR_WEB_ORIGIN = "https://qrorder.datavision-online.eu";
// Light client-side normalization (backend is the source of truth for validation/uniqueness).
const normSlug = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
const SLUG_ERRORS = {
  slug_required: "Zadajte identifikátor (slug).",
  slug_too_short: "Slug musí mať aspoň 2 znaky.",
  slug_too_long: "Slug môže mať najviac 40 znakov.",
  slug_invalid_format: "Povolené sú len malé písmená, čísla a pomlčky.",
  slug_reserved: "Tento slug je rezervovaný, zvoľte iný.",
  slug_taken: "Tento slug už používa iný zákazník.",
};

const QR_DEVICE_TYPE = "qr";

/**
 * Tab „Externé objednávky" v Nastaveniach zákazníka.
 *
 * 1) DSOK-442 — príjem objednávok z externých systémov (audioEcho, WooCommerce) cez
 *    POST /api/v1/orders/external. Autentifikácia je **Bearer JWT**. Uložené v
 *    dsokSettings.customer.externalOrders.enabled.
 *
 * 2) DSOK-475 — QR objednávanie zo stola (ďalšia sekcia nižšie). Hlavný prepínač
 *    `isActive` sprístupní nastavenia, mapu stolov a QR objednávkové API pre hostí.
 *    Uložené v dsokSettings.customer.qrOrders.*. Besteron tajomstvá šifruje backend.
 */
const ExternalOrdersTab = ({ readOnly = false }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { values, setFieldValue } = useFormikContext();

  const enabled = values?.customer?.externalOrders?.enabled === true;
  const qr = values?.customer?.qrOrders || {};
  const qrActive = qr.isActive === true;
  const editedCustomerId = values?.customer?.customerId;

  // F2: the edited customer's menus (code + title) for the DINE_IN/TAKEAWAY assignment selects.
  const [menuOptions, setMenuOptions] = React.useState([]);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let token;
        try {
          token = await generateToken(editedCustomerId);
        } catch {
          token = undefined;
        }
        const list = await listCustomerMenus(token);
        if (alive) setMenuOptions(Array.isArray(list) ? list : []);
      } catch {
        /* non-fatal: selects just show the fallback option */
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedCustomerId]);
  // Label for a menu option (multilang title falls back to the code).
  const menuLabel = (m) => {
    const title = m && m.title;
    if (title && typeof title === "object") {
      return title.sk || title.en || title.cs || Object.values(title)[0] || m.code;
    }
    return (typeof title === "string" && title) || (m && m.code) || "";
  };

  // F2 WEB ordering (customer.web). Saved via the customer save; domains (host mapping) are saved
  // separately through /api/v1/qr/web/domains (Mongo).
  const web = values?.customer?.web || {};
  const WEB_MODE_KEYS = ["delivery", "take", "dine"];
  const webModesObj = () => {
    const m = web.modes || {};
    return { dine: m.dine === true, take: m.take === true, delivery: m.delivery === true };
  };
  const toggleWebMode = (mode, on) => {
    const next = { ...webModesObj(), [mode]: on };
    if (!next.dine && !next.take && !next.delivery) next.take = true; // keep at least one
    setFieldValue("customer.web.modes", next);
  };
  const [webDomains, setWebDomainsState] = React.useState([]);
  const [webSlug, setWebSlug] = React.useState(null);
  const [domBusy, setDomBusy] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let token;
        try { token = await generateToken(editedCustomerId); } catch { token = undefined; }
        const d = await getWebDomains(token);
        if (alive) { setWebDomainsState(Array.isArray(d.domains) ? d.domains : []); setWebSlug(d.slug || null); }
      } catch {
        /* non-fatal */
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedCustomerId]);
  const saveDomains = async () => {
    setDomBusy(true);
    try {
      let token;
      try { token = await generateToken(editedCustomerId); } catch { token = undefined; }
      const clean = webDomains
        .map((d) => ({ host: String(d.host || "").trim(), pathPrefix: String(d.pathPrefix || "").trim() }))
        .filter((d) => d.host);
      const saved = await setWebDomains(clean, token);
      setWebDomainsState(Array.isArray(saved) ? saved : []);
      enqueueSnackbar(t("pages.dsokDetails.web.domainsSaved", { defaultValue: "Domény webu uložené." }), { variant: "success" });
    } catch (e) {
      const code = e?.response?.data?.status;
      enqueueSnackbar(
        code === "domain_taken"
          ? t("pages.dsokDetails.web.domainTaken", { defaultValue: "Doménu už používa iný zákazník." })
          : t("pages.dsokDetails.web.domainError", { defaultValue: "Doménu sa nepodarilo uložiť (skontrolujte formát)." }),
        { variant: "error" }
      );
    } finally {
      setDomBusy(false);
    }
  };
  // „Otvoriť web" URLs: our-domain (slug) + each custom domain.
  const WEB_BASE = (process.env.REACT_APP_WEB_ORDER_URL || "https://qrorder.datavision-online.eu").replace(/\/+$/, "");
  const webUrls = () => {
    const urls = [];
    if (webSlug) urls.push(`${WEB_BASE}/${webSlug}?web=1`);
    webDomains.forEach((d) => { if (d.host) urls.push(`https://${d.host}${d.pathPrefix || ""}`); });
    return urls;
  };

  // Receipt delivery is configured PER payment type (online / staff) as the methods the guest may
  // pick on the QR web. Normalize the stored value (legacy flat array applies to both payment types).
  const deliveryObj = () => {
    const dv = qr?.fiscal?.delivery;
    if (Array.isArray(dv)) return { online: dv, staff: dv };
    return {
      online: Array.isArray(dv?.online) ? dv.online : ["print", "email"],
      staff: Array.isArray(dv?.staff) ? dv.staff : ["print", "email"],
    };
  };
  const toggleDelivery = (type, method, on) => {
    const cur = deliveryObj();
    const set = new Set(cur[type] || []);
    if (on) set.add(method);
    else set.delete(method);
    let arr = Array.from(set);
    if (arr.length === 0) arr = ["print"]; // at least one method must stay selected
    setFieldValue("customer.qrOrders.fiscal.delivery", { ...cur, [type]: arr });
  };

  // Legacy fulfillment modes (table/pickup/delivery) are kept only for deriving F2 modes for display
  // when `modes` is not set yet (below). The AdminUI no longer edits `usage` directly.
  const usageArr = () => (Array.isArray(qr.usage) && qr.usage.length ? qr.usage : ["table"]);

  // F2: consumption modes (dine = „Na mieste", take = „So sebou"). At least one stays enabled.
  // Falls back to deriving from the legacy `usage` for display when `modes` is not set yet.
  const modesObj = () => {
    const m = qr.modes;
    if (m && typeof m === "object") return { dine: m.dine === true, take: m.take === true };
    const u = usageArr();
    const derived = { dine: u.includes("table"), take: u.includes("pickup") };
    if (!derived.dine && !derived.take) derived.dine = true;
    return derived;
  };
  const toggleMode = (mode, on) => {
    const next = { ...modesObj(), [mode]: on };
    if (!next.dine && !next.take) return; // keep at least one enabled
    setFieldValue("customer.qrOrders.modes", next);
  };

  // Pay-at-staff tenders offered to the guest (cash / card) → sent to eKasa Portos as the payment type.
  const staffMethodsArr = () =>
    Array.isArray(qr?.payments?.staffMethods) && qr.payments.staffMethods.length
      ? qr.payments.staffMethods
      : ["cash", "card"];
  const toggleStaffMethod = (method, on) => {
    const set = new Set(staffMethodsArr());
    if (on) set.add(method);
    else set.delete(method);
    let arr = ["cash", "card"].filter((m) => set.has(m));
    if (arr.length === 0) arr = ["cash"]; // at least one tender must stay enabled
    setFieldValue("customer.qrOrders.payments.staffMethods", arr);
  };

  // QR orders need a device of type „QR objednávky" (deviceType:'qr') from the Zariadenia section
  // (its uuid + location drive the order, like a kiosk). The operator picks WHICH qr device is used
  // here; the choice is stored in qrOrders.deviceId (backend resolveQrDevice prefers it). Options are
  // the customer's qr devices; the value mirrors the backend id (device.id, else the map key).
  const qrDeviceOptions = React.useMemo(() => {
    const devices = values?.customer?.devices || {};
    return Object.entries(devices)
      .filter(([, d]) => d && String(d.deviceType).toLowerCase() === QR_DEVICE_TYPE)
      .map(([key, d]) => {
        const id = d.id != null ? d.id : Number(key) || key;
        const name = d.deviceName && String(d.deviceName).trim() ? String(d.deviceName).trim() : `Zariadenie ${id}`;
        return { id, value: String(id), label: d.uuid ? `${name} · ${d.uuid}` : name };
      });
  }, [values?.customer?.devices]);
  const selectedDeviceId = qr.deviceId != null ? String(qr.deviceId) : "";
  const deviceSelected = qrDeviceOptions.some((o) => o.value === selectedDeviceId);

  // QR admin endpoints derive customerId from the JWT, so bind to the EDITED customer's token
  // (minted via generateToken) — otherwise the slug/besteron would be saved under the admin's account.
  const tokenForEdited = async () => {
    if (!editedCustomerId) return undefined;
    try { return await generateToken(editedCustomerId); } catch { return undefined; }
  };

  const [slugInput, setSlugInput] = React.useState(qr.slug || "");
  const [slugBusy, setSlugBusy] = React.useState(false);
  React.useEffect(() => { setSlugInput(qr.slug || ""); }, [qr.slug]);

  // Besteron payment-gateway credentials (encrypted by the backend; plaintext never persists).
  const besteron = qr.besteron || {};
  const besteronSet = !!(besteron.gateSecretEnc && besteron.apiSecretEnc);
  const onlinePay = qr?.payments?.online === true;
  const [bst, setBst] = React.useState({ cid: besteron.cid || "", environment: besteron.environment || "sandbox", gateSecret: "", apiSecret: "" });
  const [bstBusy, setBstBusy] = React.useState(false);
  // QR settings sub-tab (by topic): 0 = Všeobecné, 1 = Prihlásenie hostí, 2 = Platby a obsluha,
  // 3 = Doklad a doručenie. Only the active group renders; the rest render null (so each tab shows
  // its items in order even though the source keeps the original layout).
  const [qrTab, setQrTab] = React.useState(0);
  React.useEffect(() => {
    setBst((b) => ({ ...b, cid: qr?.besteron?.cid || "", environment: qr?.besteron?.environment || "sandbox" }));
  }, [qr?.besteron?.cid, qr?.besteron?.environment]);

  const saveBesteron = async () => {
    if (!bst.cid.trim() || !bst.gateSecret.trim() || !bst.apiSecret.trim()) {
      enqueueSnackbar(t("pages.dsokDetails.qrOrders.besteronMissing", { defaultValue: "Vyplňte CID a oba kľúče (Gate aj API)." }), { variant: "warning" });
      return;
    }
    setBstBusy(true);
    try {
      const blob = await encryptBesteron({ cid: bst.cid.trim(), gateSecret: bst.gateSecret.trim(), apiSecret: bst.apiSecret.trim(), environment: bst.environment }, await tokenForEdited());
      setFieldValue("customer.qrOrders.besteron", blob);
      setBst((b) => ({ ...b, gateSecret: "", apiSecret: "" }));
      enqueueSnackbar(t("pages.dsokDetails.qrOrders.besteronSaved", { defaultValue: "Prístupy k platobnej bráne uložené." }), { variant: "success" });
    } catch (e) {
      enqueueSnackbar(t("pages.dsokDetails.qrOrders.besteronError", { defaultValue: "Nepodarilo sa uložiť prístupy." }), { variant: "error" });
    } finally {
      setBstBusy(false);
    }
  };

  // Save the slug to the backend mapping (validates + guards uniqueness), then into the form.
  const saveSlug = async () => {
    const normalized = normSlug(slugInput);
    if (!normalized) { enqueueSnackbar(SLUG_ERRORS.slug_required, { variant: "warning" }); return; }
    setSlugBusy(true);
    try {
      const saved = await setQrSlug(normalized, await tokenForEdited());
      setFieldValue("customer.qrOrders.slug", saved);
      setSlugInput(saved);
      enqueueSnackbar(t("pages.dsokDetails.qrOrders.slugSaved", { defaultValue: "URL slug uložený." }), { variant: "success" });
    } catch (e) {
      const code = e?.response?.data?.status;
      enqueueSnackbar(SLUG_ERRORS[code] || t("messages.somethingWentWrong", { defaultValue: "Nepodarilo sa uložiť." }), { variant: "error" });
    } finally {
      setSlugBusy(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert variant="outlined" severity="info">
          {t("pages.dsokDetails.externalOrders.info", {
            defaultValue:
              "Príjem objednávok z externých systémov (audioEcho, WooCommerce) cez POST /api/v1/orders/external. Autentifikácia je cez Bearer token (rovnako ako celé verejné API v1).",
          })}
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              disabled={readOnly}
              onChange={(e) =>
                setFieldValue(
                  "customer.externalOrders.enabled",
                  e.target.checked,
                )
              }
            />
          }
          label={t("pages.dsokDetails.externalOrders.enabled", {
            defaultValue: "Povoliť externé objednávky",
          })}
        />
      </Grid>

      {/* --- QR objednávanie (DSOK-475) --- */}
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
        <Typography variant="h5" sx={{ mb: 1 }}>
          {t("pages.dsokDetails.qrOrders.sectionTitle", {
            defaultValue: "QR objednávanie",
          })}
        </Typography>
        <Alert variant="outlined" severity="info">
          {t("pages.dsokDetails.qrOrders.info", {
            defaultValue:
              "QR objednávanie zo stola. Po zapnutí sprístupníte nastavenia, mapu stolov a QR objednávkové API pre hostí.",
          })}
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={qrActive}
              disabled={readOnly}
              onChange={(e) =>
                setFieldValue("customer.qrOrders.isActive", e.target.checked)
              }
            />
          }
          label={t("pages.dsokDetails.qrOrders.isActive", {
            defaultValue: "QR objednávanie",
          })}
        />
      </Grid>

      {qrActive ? (
        <>
          {/* QR settings grouped into sub-tabs by topic (prehľadnosť). Only the active group renders. */}
          <Grid item xs={12}>
            <Tabs
              value={qrTab}
              onChange={(_, v) => setQrTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label={t("pages.dsokDetails.qrOrders.tabGeneral", { defaultValue: "Všeobecné" })} />
              <Tab label={t("pages.dsokDetails.qrOrders.tabAuth", { defaultValue: "Prihlásenie hostí" })} />
              <Tab label={t("pages.dsokDetails.qrOrders.tabPayments", { defaultValue: "Platby a obsluha" })} />
              <Tab label={t("pages.dsokDetails.qrOrders.tabReceipt", { defaultValue: "Doklad a doručenie" })} />
              <Tab label={t("pages.dsokDetails.web.tab", { defaultValue: "Web objednávanie" })} />
            </Tabs>
          </Grid>

          {qrTab === 0 ? (
            <>
          {/* QR device (deviceType 'qr') — the operator selects which qr device from Zariadenia is
              used; its uuid + location drive the order, like a kiosk. Stored in qrOrders.deviceId. */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t("pages.dsokDetails.qrOrders.deviceTitle", { defaultValue: "QR zariadenie (eKasa / tlač)" })}
            </Typography>
            {qrDeviceOptions.length === 0 ? (
              <Alert variant="outlined" severity="warning">
                {t("pages.dsokDetails.qrOrders.deviceNone", {
                  defaultValue: "Žiadne zariadenie typu „QR objednávky“. V sekcii Správa zariadení pridajte zariadenie a nastavte mu Typ zariadenia = „QR objednávky“, potom ho tu vyberte.",
                })}
              </Alert>
            ) : (
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={7} md={5}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    disabled={readOnly}
                    label={t("pages.dsokDetails.qrOrders.deviceSelect", { defaultValue: "Zariadenie pre QR objednávky" })}
                    value={deviceSelected ? selectedDeviceId : ""}
                    onChange={(e) => setFieldValue("customer.qrOrders.deviceId", Number(e.target.value))}
                  >
                    {qrDeviceOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Chip
                    size="small"
                    color={deviceSelected ? "success" : "warning"}
                    label={deviceSelected
                      ? t("pages.dsokDetails.qrOrders.deviceReady", { defaultValue: "nastavené" })
                      : t("pages.dsokDetails.qrOrders.deviceNotSelected", { defaultValue: "nevybraté" })}
                  />
                </Grid>
              </Grid>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {t("pages.dsokDetails.qrOrders.deviceHint", {
                defaultValue: "UUID a lokalita vybraného zariadenia sa použijú pri objednávke, rovnako ako pri kiosku.",
              })}
            </Typography>
          </Grid>

          {/* Public URL slug — identifies this customer in the shared QR web domain. */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t("pages.dsokDetails.qrOrders.slugLabel", { defaultValue: "URL identifikátor (slug)" })}
            </Typography>
            <Grid container spacing={1} alignItems="flex-start">
              <Grid item xs={12} sm={7} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  disabled={readOnly}
                  placeholder="napr. intesso"
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value)}
                  onBlur={(e) => setSlugInput(normSlug(e.target.value))}
                  InputProps={{ startAdornment: <InputAdornment position="start">/</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <Button variant="outlined" disabled={readOnly || slugBusy} onClick={saveSlug} sx={{ height: 40 }}>
                  {t("pages.dsokDetails.qrOrders.slugSave", { defaultValue: "Uložiť slug" })}
                </Button>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {`${QR_WEB_ORIGIN}/${normSlug(slugInput) || "{slug}"}/t/{token}`}
            </Typography>
          </Grid>

          {/* Logo shown on the QR guest web (header + welcome). Falls back to the customer main logo. */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t("pages.dsokDetails.qrOrders.logo", { defaultValue: "Logo (QR web)" })}
            </Typography>
            <UploadComponent
              name="customer.qrOrders.logo"
              setFieldValue={setFieldValue}
              value={qr.logo || ""}
              onUpload={(img) => uploadToImagekit(img, "dsok/logo")}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {t("pages.dsokDetails.qrOrders.logoHint", {
                defaultValue:
                  "Zobrazí sa na QR stránke pre hosťa. Odporúčame štvorcové logo (1:1), ideálne 512×512 px (min. 256×256), PNG s priehľadným pozadím alebo SVG (aj JPG). Max 5 MB. Ak nie je nastavené, použije sa hlavné logo zákazníka.",
              })}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={qr.enabled === true}
                  disabled={readOnly}
                  onChange={(e) =>
                    setFieldValue("customer.qrOrders.enabled", e.target.checked)
                  }
                />
              }
              label={t("pages.dsokDetails.qrOrders.enabled", {
                defaultValue: "Spustené pre hostí",
              })}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={qr.kitchenNote === true}
                  disabled={readOnly}
                  onChange={(e) =>
                    setFieldValue(
                      "customer.qrOrders.kitchenNote",
                      e.target.checked,
                    )
                  }
                />
              }
              label={t("pages.dsokDetails.qrOrders.kitchenNote", {
                defaultValue: "Pole „Poznámka do kuchyne“",
              })}
            />
          </Grid>
            </>
          ) : null}

          {qrTab === 1 ? (
            <>
          {/* Guest sign-in methods. Google/Facebook also need the platform OAuth client id on the
              backend (QR_GOOGLE_CLIENT_ID); without it the provider button does not render. */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t("pages.dsokDetails.qrOrders.authTitle", { defaultValue: "Prihlásenie hostí" })}
            </Typography>
            <Stack direction="row" flexWrap="wrap">
              {[
                ["email", "E-mailom", true],
                ["google", "Google", true],
                ["facebook", "Facebook", true],
              ].map(([key, label, def]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Switch
                      checked={qr?.auth?.[key] !== false}
                      disabled={readOnly}
                      onChange={(e) => setFieldValue(`customer.qrOrders.auth.${key}`, e.target.checked)}
                    />
                  }
                  label={t(`pages.dsokDetails.qrOrders.auth_${key}`, { defaultValue: label })}
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {t("pages.dsokDetails.qrOrders.authHint", {
                defaultValue: "Google/Facebook sa zobrazia len keď je na serveri nastavený platformový OAuth Client ID.",
              })}
            </Typography>
          </Grid>
            </>
          ) : null}

          {qrTab === 2 ? (
            <>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={qr?.payments?.online === true}
                  disabled={readOnly}
                  onChange={(e) =>
                    setFieldValue(
                      "customer.qrOrders.payments.online",
                      e.target.checked,
                    )
                  }
                />
              }
              label={t("pages.dsokDetails.qrOrders.paymentOnline", {
                defaultValue: "Online platba",
              })}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={qr?.payments?.staff === true}
                  disabled={readOnly}
                  onChange={(e) =>
                    setFieldValue(
                      "customer.qrOrders.payments.staff",
                      e.target.checked,
                    )
                  }
                />
              }
              label={t("pages.dsokDetails.qrOrders.paymentStaff", {
                defaultValue: "Platba u obsluhy",
              })}
            />
          </Grid>

          {/* Table-service actions shown in the guest app's „Viac" menu. „Účet, prosím" only
              appears in the app when pay-at-staff is enabled (the backend gates it). */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t("pages.dsokDetails.qrOrders.callStaffTitle", { defaultValue: "Obsluha" })}
            </Typography>
            <Stack direction="row" flexWrap="wrap">
              <Tooltip
                arrow
                title={t("pages.dsokDetails.qrOrders.callWaiterHelp", {
                  defaultValue: "Zobrazí v aplikácii pre hosťa tlačidlo „Zavolať čašníka“.",
                })}
              >
                <Box component="span">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={qr?.callStaff?.waiter === true}
                        disabled
                        onChange={(e) =>
                          setFieldValue("customer.qrOrders.callStaff.waiter", e.target.checked)
                        }
                      />
                    }
                    label={t("pages.dsokDetails.qrOrders.callWaiter", {
                      defaultValue: "Zavolať čašníka",
                    })}
                  />
                </Box>
              </Tooltip>
              <Tooltip
                arrow
                title={t("pages.dsokDetails.qrOrders.callBillHelp", {
                  defaultValue:
                    "Zobrazí tlačidlo „Účet, prosím“. V aplikácii sa ukáže len pri platbe u obsluhy.",
                })}
              >
                <Box component="span">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={qr?.callStaff?.bill === true}
                        disabled
                        onChange={(e) =>
                          setFieldValue("customer.qrOrders.callStaff.bill", e.target.checked)
                        }
                      />
                    }
                    label={t("pages.dsokDetails.qrOrders.callBill", {
                      defaultValue: "Účet, prosím",
                    })}
                  />
                </Box>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {t("pages.dsokDetails.qrOrders.callStaffComingSoon", {
                defaultValue: "Túto funkcionalitu pripravujeme – čoskoro ju sprístupníme.",
              })}
            </Typography>
          </Grid>

          {/* Pay-at-staff tenders (cash / card) → sent to eKasa Portos as the payment type. Shown
              when pay-at-staff is enabled; the guest picks among the allowed ones in the app. */}
          {qr?.payments?.staff === true ? (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("pages.dsokDetails.qrOrders.staffMethodsTitle", {
                  defaultValue: "Platba u obsluhy – spôsob",
                })}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={staffMethodsArr().includes("cash")}
                    disabled={readOnly}
                    onChange={(e) => toggleStaffMethod("cash", e.target.checked)}
                  />
                }
                label={t("pages.dsokDetails.qrOrders.staffCash", { defaultValue: "Hotovosť" })}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={staffMethodsArr().includes("card")}
                    disabled={readOnly}
                    onChange={(e) => toggleStaffMethod("card", e.target.checked)}
                  />
                }
                label={t("pages.dsokDetails.qrOrders.staffCard", { defaultValue: "Platobná karta" })}
              />
            </Grid>
          ) : null}
            </>
          ) : null}

          {qrTab === 3 ? (
            <>
          {/* Receipt delivery per payment type: for each type the admin allows print and/or email;
              the QR web lets the guest choose among the allowed methods (or forces the single one). */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
              {t("pages.dsokDetails.qrOrders.deliveryTitle", {
                defaultValue: "Výdaj dokladu",
              })}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t("pages.dsokDetails.qrOrders.deliveryHint", {
                defaultValue:
                  "Pre každý typ platby vyberte, aké spôsoby doručenia dokladu môže hosť použiť. Ak povolíte oba, hosť si vyberie; ak jeden, je vynútený.",
              })}
            </Typography>
          </Grid>
          {[
            ["online", t("pages.dsokDetails.qrOrders.paymentOnline", { defaultValue: "Online platba" })],
            ["staff", t("pages.dsokDetails.qrOrders.paymentStaff", { defaultValue: "Platba u obsluhy" })],
          ].map(([type, label]) => (
            <Grid item xs={12} md={6} key={`delivery-${type}`}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {label}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deliveryObj()[type].includes("print")}
                    disabled={readOnly}
                    onChange={(e) => toggleDelivery(type, "print", e.target.checked)}
                  />
                }
                label={t("pages.dsokDetails.qrOrders.deliveryPrint", {
                  defaultValue: "Tlačiť doklad",
                })}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deliveryObj()[type].includes("email")}
                    disabled={readOnly}
                    onChange={(e) => toggleDelivery(type, "email", e.target.checked)}
                  />
                }
                label={t("pages.dsokDetails.qrOrders.deliveryEmail", {
                  defaultValue: "Zaslať e-mailom",
                })}
              />
            </Grid>
          ))}

          {/* F2: order mode (Na mieste / So sebou) + per-type menu assignment. Replaces the legacy
              fulfillment modes (table/pickup/delivery). Guest picks the mode at order start; a single
              enabled mode is used automatically. Each mode's consumption type selects its own menu. */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
              {t("pages.dsokDetails.qrOrders.modesTitle", { defaultValue: "Spôsob objednávky" })}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t("pages.dsokDetails.qrOrders.modesHint", {
                defaultValue:
                  "Na začiatku objednávky si hosť vyberie spôsob. Ak je zapnutý len jeden, výber sa nezobrazí a použije sa automaticky. Každý spôsob má priradené vlastné menu.",
              })}
            </Typography>
          </Grid>
          {[
            ["dine", t("pages.dsokDetails.qrOrders.modeDine", { defaultValue: "Na mieste" })],
            ["take", t("pages.dsokDetails.qrOrders.modeTake", { defaultValue: "So sebou" })],
          ].map(([mode, label]) => (
            <Grid item xs={12} md={6} key={`mode-${mode}`}>
              <FormControlLabel
                control={
                  <Switch
                    checked={modesObj()[mode]}
                    disabled={readOnly}
                    onChange={(e) => toggleMode(mode, e.target.checked)}
                  />
                }
                label={label}
              />
            </Grid>
          ))}
          {[
            ["dineIn", t("pages.dsokDetails.qrOrders.menuDineIn", { defaultValue: "Menu pre Na mieste (DINE_IN)" })],
            ["takeaway", t("pages.dsokDetails.qrOrders.menuTakeaway", { defaultValue: "Menu pre So sebou (TAKEAWAY)" })],
          ].map(([key, label]) => (
            <Grid item xs={12} md={6} key={`menuassign-${key}`}>
              <TextField
                select
                fullWidth
                size="small"
                disabled={readOnly}
                label={label}
                value={qr?.menuAssign?.[key] || ""}
                onChange={(e) =>
                  setFieldValue(`customer.qrOrders.menuAssign.${key}`, e.target.value || null)
                }
              >
                <MenuItem value="">
                  {t("pages.dsokDetails.qrOrders.menuNone", { defaultValue: "— predvolené menu (QR) —" })}
                </MenuItem>
                {menuOptions.map((m) => (
                  <MenuItem key={m.code} value={m.code}>
                    {menuLabel(m)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ))}
            </>
          ) : null}

          {qrTab === 2 ? (
            <>
          <Grid item xs={12} md={6}>
            <TextField
              type="number"
              fullWidth
              size="small"
              disabled={readOnly}
              label={t("pages.dsokDetails.qrOrders.orderExpiryMinutes", {
                defaultValue: "Expirácia online platby (min)",
              })}
              value={qr.orderExpiryMinutes ?? 15}
              onChange={(e) =>
                setFieldValue(
                  "customer.qrOrders.orderExpiryMinutes",
                  Number(e.target.value),
                )
              }
            />
          </Grid>

          {/* Besteron payment-gateway credentials — managed here (same section as the slug), NOT in the table map. */}
          {onlinePay ? (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t("pages.dsokDetails.qrOrders.besteronTitle", { defaultValue: "Platobná brána – prístupy" })}
                </Typography>
                <Chip
                  size="small"
                  color={besteronSet ? "success" : "default"}
                  label={besteronSet
                    ? t("pages.dsokDetails.qrOrders.besteronConfigured", { defaultValue: "nastavené" })
                    : t("pages.dsokDetails.qrOrders.besteronNotConfigured", { defaultValue: "nenastavené" })}
                />
              </Stack>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth size="small" disabled={readOnly}
                    label={t("pages.dsokDetails.qrOrders.besteronCid", { defaultValue: "CID (merchant)" })}
                    value={bst.cid}
                    onChange={(e) => setBst({ ...bst, cid: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select fullWidth size="small" disabled={readOnly}
                    label={t("pages.dsokDetails.qrOrders.besteronEnv", { defaultValue: "Prostredie" })}
                    value={bst.environment}
                    onChange={(e) => setBst({ ...bst, environment: e.target.value })}
                  >
                    <MenuItem value="sandbox">sandbox</MenuItem>
                    <MenuItem value="production">production</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={5}>
                  <TextField
                    fullWidth size="small" type="password" autoComplete="new-password" disabled={readOnly}
                    label={t("pages.dsokDetails.qrOrders.besteronGate", { defaultValue: "Gate secret" })}
                    placeholder={besteronSet ? "•••••• (zadajte pre zmenu)" : ""}
                    value={bst.gateSecret}
                    onChange={(e) => setBst({ ...bst, gateSecret: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={5}>
                  <TextField
                    fullWidth size="small" type="password" autoComplete="new-password" disabled={readOnly}
                    label={t("pages.dsokDetails.qrOrders.besteronApi", { defaultValue: "API secret" })}
                    placeholder={besteronSet ? "•••••• (zadajte pre zmenu)" : ""}
                    value={bst.apiSecret}
                    onChange={(e) => setBst({ ...bst, apiSecret: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Button variant="outlined" disabled={readOnly || bstBusy} onClick={saveBesteron} sx={{ height: 40 }}>
                    {t("pages.dsokDetails.qrOrders.besteronSave", { defaultValue: "Uložiť prístupy" })}
                  </Button>
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {t("pages.dsokDetails.qrOrders.besteronHint", {
                  defaultValue: "Kľúče sa zašifrujú na serveri a nikdy sa nezobrazujú späť. Pri zmene zadajte oba kľúče znova.",
                })}
              </Typography>
            </Grid>
          ) : null}
            </>
          ) : null}

          {/* F2 WEB objednávanie (e-shop) — rovnaká appka pre počítač/tablet/mobil. Vlastná podzáložka. */}
          {qrTab === 4 ? (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t("pages.dsokDetails.web.title", { defaultValue: "Web objednávanie (e-shop)" })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              {t("pages.dsokDetails.web.info", {
                defaultValue: "Rovnaká ponuka ako QR, ale pre web (bez stola). Rozvoz / So sebou / Na mieste.",
              })}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={web.enabled === true}
                  disabled={readOnly}
                  onChange={(e) => setFieldValue("customer.web.enabled", e.target.checked)}
                />
              }
              label={t("pages.dsokDetails.web.enabled", { defaultValue: "Povoliť web objednávanie" })}
            />
            {web.enabled ? (
              <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("pages.dsokDetails.web.modesTitle", { defaultValue: "Spôsoby na webe" })}
                  </Typography>
                  {WEB_MODE_KEYS.map((m) => (
                    <FormControlLabel
                      key={m}
                      control={
                        <Switch
                          size="small"
                          checked={webModesObj()[m]}
                          disabled={readOnly}
                          onChange={(e) => toggleWebMode(m, e.target.checked)}
                        />
                      }
                      label={t(`pages.dsokDetails.web.mode_${m}`, {
                        defaultValue: m === "delivery" ? "Rozvoz" : m === "take" ? "So sebou" : "Na mieste",
                      })}
                    />
                  ))}
                </Grid>
                {[
                  ["dineIn", t("pages.dsokDetails.web.menuDineIn", { defaultValue: "Menu pre Na mieste" }), ""],
                  ["takeaway", t("pages.dsokDetails.web.menuTakeaway", { defaultValue: "Menu pre So sebou" }), ""],
                  [
                    "delivery",
                    t("pages.dsokDetails.web.menuDelivery", { defaultValue: "Menu pre Rozvoz" }),
                    t("pages.dsokDetails.web.menuDeliveryHint", {
                      defaultValue: "Ak nevyberiete, pre rozvoz sa použije menu „So sebou“.",
                    }),
                  ],
                ].map(([key, label, hint]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      disabled={readOnly}
                      label={label}
                      helperText={hint || undefined}
                      value={web?.menuAssign?.[key] || ""}
                      onChange={(e) => setFieldValue(`customer.web.menuAssign.${key}`, e.target.value || null)}
                    >
                      <MenuItem value="">
                        {t("pages.dsokDetails.web.menuNone", { defaultValue: "— predvolené menu (QR) —" })}
                      </MenuItem>
                      {menuOptions.map((m) => (
                        <MenuItem key={m.code} value={m.code}>
                          {menuLabel(m)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                ))}
                {/* Cena rozvozu + min. objednávka sa nastavujú v „QR objednávanie → Nastavenia →
                    Web nastavenia" (dsokQrHighlights.web.delivery). Tu už nie sú. */}

                {/* Per-mód platba + doklad (customer.web.modeConfig). */}
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("pages.dsokDetails.web.payReceiptTitle", { defaultValue: "Platba a doklad (podľa spôsobu)" })}
                  </Typography>
                  {WEB_MODE_KEYS.filter((m) => webModesObj()[m]).map((m) => {
                    const cfg = (web.modeConfig && web.modeConfig[m]) || {};
                    const orc = cfg.onlineReceipt || {};
                    const val = (v) => v !== false; // default true
                    const setCfg = (k, v) => setFieldValue(`customer.web.modeConfig.${m}.${k}`, v);
                    const setRc = (k, v) => setFieldValue(`customer.web.modeConfig.${m}.onlineReceipt.${k}`, v);
                    const handoverLabel =
                      m === "delivery"
                        ? t("pages.dsokDetails.web.payCourier", { defaultValue: "Platba kuriérovi" })
                        : t("pages.dsokDetails.web.payOnSite", { defaultValue: "Platba obsluhe / na mieste" });
                    const physicalLabel =
                      m === "delivery"
                        ? t("pages.dsokDetails.web.docCourier", { defaultValue: "Doklad kuriérom" })
                        : t("pages.dsokDetails.web.docOnSite", { defaultValue: "Doklad na mieste" });
                    const modeLabel = t(`pages.dsokDetails.web.mode_${m}`, {
                      defaultValue: m === "delivery" ? "Rozvoz" : m === "take" ? "So sebou" : "Na mieste",
                    });
                    return (
                      <Box key={m} sx={{ border: "1px solid #e0ddd6", borderRadius: 1, p: 1, mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>{modeLabel}</Typography>
                        {/* Online payment + its receipt options (e-mail / physical). */}
                        <FormControlLabel control={<Checkbox size="small" checked={val(cfg.payOnline)} disabled={readOnly} onChange={(e) => setCfg("payOnline", e.target.checked)} />} label={t("pages.dsokDetails.web.payOnline", { defaultValue: "Platba online" })} />
                        {val(cfg.payOnline) ? (
                          <Box sx={{ ml: 3, mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              {t("pages.dsokDetails.web.onlineReceiptTitle", { defaultValue: "Doklad pri online platbe:" })}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              <FormControlLabel control={<Checkbox size="small" checked={val(orc.email)} disabled={readOnly} onChange={(e) => setRc("email", e.target.checked)} />} label={t("pages.dsokDetails.web.receiptEmail", { defaultValue: "Doklad e-mailom" })} />
                              <FormControlLabel control={<Checkbox size="small" checked={val(orc.physical)} disabled={readOnly} onChange={(e) => setRc("physical", e.target.checked)} />} label={physicalLabel} />
                            </Box>
                          </Box>
                        ) : null}
                        {/* Pay at handover — receipt is always physical (no sub-option). */}
                        <FormControlLabel control={<Checkbox size="small" checked={val(cfg.payHandover)} disabled={readOnly} onChange={(e) => setCfg("payHandover", e.target.checked)} />} label={handoverLabel} />
                        {val(cfg.payHandover) ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 3 }}>
                            {t("pages.dsokDetails.web.handoverReceiptNote", { defaultValue: "Doklad sa vystaví na mieste / kuriérom." })}
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  })}
                </Grid>

                {/* Výber času objednávky + otváracie hodiny sa nastavujú v „QR objednávanie → Nastavenia →
                    Web nastavenia" (uložené v dsokQrHighlights). Tu už nie sú. */}

                {/* Vlastné domény (uložené zvlášť — mimo customer save). */}
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("pages.dsokDetails.web.domainsTitle", { defaultValue: "Vlastné domény (voliteľné)" })}
                  </Typography>
                  {webDomains.map((d, i) => (
                    <Grid container spacing={1} key={i} sx={{ mb: 0.5 }} alignItems="center">
                      <Grid item xs={7} sm={6}>
                        <TextField
                          fullWidth size="small" disabled={readOnly}
                          placeholder="napr. eshop.mojarestauracia.sk"
                          label={t("pages.dsokDetails.web.domainHost", { defaultValue: "Doména / subdoména" })}
                          value={d.host || ""}
                          onChange={(e) => setWebDomainsState(webDomains.map((x, j) => (j === i ? { ...x, host: e.target.value } : x)))}
                        />
                      </Grid>
                      <Grid item xs={4} sm={4}>
                        <TextField
                          fullWidth size="small" disabled={readOnly}
                          placeholder="/order"
                          label={t("pages.dsokDetails.web.domainPrefix", { defaultValue: "Podcesta (voliteľné)" })}
                          value={d.pathPrefix || ""}
                          onChange={(e) => setWebDomainsState(webDomains.map((x, j) => (j === i ? { ...x, pathPrefix: e.target.value } : x)))}
                        />
                      </Grid>
                      <Grid item xs={1} sm={2}>
                        <Button size="small" color="error" disabled={readOnly} onClick={() => setWebDomainsState(webDomains.filter((_, j) => j !== i))}>
                          ×
                        </Button>
                      </Grid>
                    </Grid>
                  ))}
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Button size="small" variant="outlined" disabled={readOnly} onClick={() => setWebDomainsState([...webDomains, { host: "", pathPrefix: "" }])}>
                      {t("pages.dsokDetails.web.domainAdd", { defaultValue: "+ Pridať doménu" })}
                    </Button>
                    <Button size="small" variant="contained" disabled={readOnly || domBusy} onClick={saveDomains}>
                      {t("pages.dsokDetails.web.domainsSave", { defaultValue: "Uložiť domény" })}
                    </Button>
                  </Stack>
                </Grid>

                {/* „Otvoriť web" odkazy. */}
                {webUrls().length ? (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {t("pages.dsokDetails.web.openTitle", { defaultValue: "Otvoriť web" })}
                    </Typography>
                    <Stack spacing={0.5}>
                      {webUrls().map((u) => (
                        <a key={u} href={u} target="_blank" rel="noreferrer" style={{ color: "var(--accent, #a64b0a)", fontWeight: 600 }}>
                          {u}
                        </a>
                      ))}
                    </Stack>
                  </Grid>
                ) : null}
              </Grid>
            ) : null}
          </Grid>
          ) : null}

          <Grid item xs={12}>
            <Alert severity="info">
              {t("pages.dsokDetails.qrOrders.tableMapNote", {
                defaultValue: "Mapu stolov a QR kódy spravujte v sekcii „Mapa stolov“.",
              })}
            </Alert>
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};

export default ExternalOrdersTab;
